import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "react-feather";
import type { PortalMenuRect } from "../../../hooks/usePortalMenu";
import {
  Card,
  Header,
  NavButton,
  MonthLabel,
  Grid,
  WeekdayCell,
  DayCell,
} from "./Calendar.styles";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Build ISO yyyy-mm-dd from local Y/M/D without timezone drift. */
const toIso = (y: number, m: number, d: number) =>
  `${y}-${pad2(m + 1)}-${pad2(d)}`;

/** Parse ISO yyyy-mm-dd into {y, m, d} (m is 0-based). Returns null for invalid. */
const parseIso = (iso: string | null | undefined) => {
  if (!iso) return null;
  const parts = iso.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
};

type MonthView = { y: number; m: number };

/**
 * The month a calendar with no value opens on: today's, moved inside
 * `min`/`max` when today falls outside them, so a field whose data ended years
 * ago doesn't open on a month of greyed-out days.
 */
export const toOpeningMonth = (today: MonthView, min?: string, max?: string): MonthView => {
  const key = ({ y, m }: MonthView) => y * 12 + m;
  const low = parseIso(min);
  const high = parseIso(max);
  if (high && key(today) > key(high)) return { y: high.y, m: high.m };
  if (low && key(today) < key(low)) return { y: low.y, m: low.m };
  return today;
};

interface DayEntry {
  iso: string;
  day: number;
  muted: boolean;
  disabled: boolean;
  selected: boolean;
}

const buildMonthGrid = (
  viewYear: number,
  viewMonth: number,
  selectedIso: string | null,
  minIso: string | null,
  maxIso: string | null,
): DayEntry[] => {
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const startDow = firstOfMonth.getDay(); // 0 = Sunday
  const gridStart = new Date(viewYear, viewMonth, 1 - startDow);

  const entries: DayEntry[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const iso = toIso(d.getFullYear(), d.getMonth(), d.getDate());
    const muted = d.getMonth() !== viewMonth;
    const disabled = (minIso !== null && iso < minIso) || (maxIso !== null && iso > maxIso);
    entries.push({
      iso,
      day: d.getDate(),
      muted,
      disabled,
      selected: selectedIso === iso,
    });
  }
  return entries;
};

export interface CalendarProps {
  /** Controlled ISO yyyy-mm-dd (empty means no selection). */
  value: string;
  onSelect: (iso: string) => void;
  min?: string;
  max?: string;
  /** Whether the popover is open (controls portal mount). */
  open: boolean;
  /** Menu ref for outside-click detection (via usePortalMenu). */
  menuRef: React.RefObject<HTMLDivElement>;
  /** Computed rect from usePortalMenu. */
  menuRect: PortalMenuRect | null;
}

export const Calendar: React.FC<CalendarProps> = ({
  value,
  onSelect,
  min,
  max,
  open,
  menuRef,
  menuRect,
}) => {
  const today = useMemo(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  }, []);

  const parsedValue = useMemo(() => parseIso(value), [value]);

  const [view, setView] = useState<MonthView>(() =>
    parsedValue ? { y: parsedValue.y, m: parsedValue.m } : toOpeningMonth(today, min, max),
  );

  /** When the selected value changes externally, re-anchor the view to it. */
  useEffect(() => {
    if (parsedValue) {
      setView({ y: parsedValue.y, m: parsedValue.m });
    }
  }, [parsedValue]);

  /** With no value, open inside the allowed range; `min`/`max` often load after the field mounts. */
  useEffect(() => {
    if (open && !parsedValue) setView(toOpeningMonth(today, min, max));
  }, [open, parsedValue, today, min, max]);

  const days = useMemo(
    () => buildMonthGrid(view.y, view.m, value || null, min ?? null, max ?? null),
    [view.y, view.m, value, min, max],
  );

  const goPrev = () => {
    setView(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  };
  const goNext = () => {
    setView(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  };

  if (!open || !menuRect) return null;

  return createPortal(
    <Card
      ref={menuRef}
      role="dialog"
      aria-label="Choose date"
      style={{ top: menuRect.top, left: menuRect.left }}
    >
      <Header>
        <NavButton
          type="button"
          aria-label="Previous month"
          onClick={goPrev}
        >
          <ChevronLeft size={16} />
        </NavButton>
        <MonthLabel aria-live="polite">
          {MONTH_NAMES[view.m]} {view.y}
        </MonthLabel>
        <NavButton
          type="button"
          aria-label="Next month"
          onClick={goNext}
        >
          <ChevronRight size={16} />
        </NavButton>
      </Header>

      <Grid role="grid">
        {WEEKDAY_LABELS.map((w) => (
          <WeekdayCell key={w} role="columnheader">
            {w}
          </WeekdayCell>
        ))}
        {days.map((d) => (
          <DayCell
            key={d.iso}
            type="button"
            role="gridcell"
            aria-selected={d.selected}
            aria-disabled={d.disabled || undefined}
            disabled={d.disabled}
            $selected={d.selected}
            $muted={d.muted}
            $disabled={d.disabled}
            onClick={() => onSelect(d.iso)}
          >
            {d.day}
          </DayCell>
        ))}
      </Grid>
    </Card>,
    document.body,
  );
};

export default Calendar;
